import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const PAGE_KEYS = {
  about: 'page_about_content',
  terms: 'page_terms_content',
  privacy: 'page_privacy_content',
};

const PAGE_LABELS = {
  about: 'من نحن',
  terms: 'الشروط والأحكام',
  privacy: 'سياسة الخصوصية',
};

const PAGE_ICONS = {
  about: 'Info',
  terms: 'FileText',
  privacy: 'Shield',
};

const PAGE_URLS = {
  about: '/about',
  terms: '/terms',
  privacy: '/privacy',
};

const TOOLBAR_ITEMS = [
  { tag: 'h2', label: 'H2', title: 'عنوان رئيسي' },
  { tag: 'h3', label: 'H3', title: 'عنوان فرعي' },
  { tag: 'p', label: 'P', title: 'فقرة نصية' },
  { tag: 'strong', label: 'B', title: 'خط عريض' },
  { tag: 'ul_li', label: '• قائمة', title: 'إضافة عنصر قائمة' },
];

export default function PagesTab() {
  const [activePage, setActivePage] = useState('about');
  const [contents, setContents] = useState({ about: '', terms: '', privacy: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadPageContents();
  }, []);

  const loadPageContents = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('app_settings')
        .select('key, value')
        .in('key', Object.values(PAGE_KEYS));

      if (fetchError) throw fetchError;

      const map = {};
      (data || []).forEach((row) => { map[row.key] = row.value; });

      setContents({
        about: map[PAGE_KEYS.about] || '',
        terms: map[PAGE_KEYS.terms] || '',
        privacy: map[PAGE_KEYS.privacy] || '',
      });
    } catch (err) {
      console.error('Failed to load page contents:', err);
      setError('فشل تحميل محتوى الصفحات');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaved(false);
      setError(null);

      const key = PAGE_KEYS[activePage];
      const value = contents[activePage];

      const { error: upsertError } = await supabase
        .from('app_settings')
        .upsert(
          { key, value, description: `محتوى صفحة ${PAGE_LABELS[activePage]}`, category: 'pages', updated_at: new Date().toISOString() },
          { onConflict: 'key', ignoreDuplicates: false }
        );

      if (upsertError) throw upsertError;

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save page content:', err);
      setError(`فشل حفظ المحتوى: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const insertTag = (tag) => {
    const textarea = document.getElementById('page-content-editor');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = textarea.value.substring(start, end);
    const before = textarea.value.substring(0, start);
    const after = textarea.value.substring(end);

    let insertion = '';
    if (tag === 'ul_li') {
      insertion = `<li>${selected || 'نص العنصر'}</li>`;
    } else {
      insertion = `<${tag}>${selected || `نص ${tag}`}</${tag}>`;
    }

    const newValue = before + insertion + after;
    setContents((prev) => ({ ...prev, [activePage]: newValue }));

    setTimeout(() => {
      textarea.focus();
      const newPos = start + insertion.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const wrapInUl = () => {
    const textarea = document.getElementById('page-content-editor');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const before = textarea.value.substring(0, start);
    const after = textarea.value.substring(start);
    const newValue = before + '\n<ul>\n  <li>العنصر الأول</li>\n  <li>العنصر الثاني</li>\n</ul>\n' + after;
    setContents((prev) => ({ ...prev, [activePage]: newValue }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>جاري تحميل الصفحات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl border-2 border-indigo-100 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Icon name="FileEdit" size={24} className="text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">إدارة الصفحات الثابتة</h2>
            <p className="text-sm text-slate-500 mt-0.5">تعديل محتوى صفحات من نحن والشروط والأحكام وسياسة الخصوصية</p>
          </div>
        </div>
      </div>

      {/* Page Selector Tabs */}
      <div className="flex gap-3">
        {Object.entries(PAGE_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setActivePage(key); setPreviewMode(false); setSaved(false); setError(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all border ${
              activePage === key
                ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
            }`}
          >
            <Icon name={PAGE_ICONS[key]} size={16} color={activePage === key ? 'white' : '#475569'} />
            {label}
          </button>
        ))}
      </div>

      {/* Editor Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Editor Toolbar */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-slate-400 ml-2">إدراج سريع:</span>
            {TOOLBAR_ITEMS.map((item) => (
              <button
                key={item.tag}
                onClick={() => item.tag === 'ul_li' ? wrapInUl() : insertTag(item.tag)}
                title={item.title}
                className="px-2.5 py-1 text-xs font-mono font-bold rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <a
              href={PAGE_URLS[activePage]}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline"
            >
              <Icon name="ExternalLink" size={13} />
              عرض الصفحة
            </a>
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                previewMode
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
              }`}
            >
              <Icon name={previewMode ? 'Code' : 'Eye'} size={13} color={previewMode ? 'white' : '#475569'} />
              {previewMode ? 'تحرير' : 'معاينة'}
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="p-4">
          {previewMode ? (
            <div
              className="min-h-[500px] p-6 prose prose-slate max-w-none text-right"
              style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
              dangerouslySetInnerHTML={{ __html: contents[activePage] || '<p class="text-slate-400">لا يوجد محتوى بعد</p>' }}
            />
          ) : (
            <textarea
              id="page-content-editor"
              value={contents[activePage]}
              onChange={(e) => setContents((prev) => ({ ...prev, [activePage]: e.target.value }))}
              className="w-full min-h-[500px] p-4 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 leading-relaxed"
              placeholder={`أدخل محتوى HTML لصفحة ${PAGE_LABELS[activePage]}...
              
مثال:
<h2>العنوان الرئيسي</h2>
<p>نص الفقرة هنا...</p>
<h3>عنوان فرعي</h3>
<p>نص آخر...</p>
<ul>
  <li>عنصر أول</li>
  <li>عنصر ثاني</li>
</ul>`}
              dir="rtl"
            />
          )}
        </div>

        {/* HTML Help */}
        {!previewMode && (
          <div className="px-4 pb-3 flex flex-wrap gap-x-4 gap-y-1">
            {[
              ['<h2>عنوان</h2>', 'عنوان رئيسي'],
              ['<h3>عنوان</h3>', 'عنوان فرعي'],
              ['<p>نص</p>', 'فقرة'],
              ['<strong>نص</strong>', 'خط عريض'],
              ['<ul><li>...</li></ul>', 'قائمة نقطية'],
            ].map(([code, desc]) => (
              <span key={code} className="text-[11px] text-slate-400">
                <code className="bg-slate-100 px-1 rounded text-slate-600">{code}</code> = {desc}
              </span>
            ))}
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex items-center gap-2">
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-green-600 font-semibold">
                <Icon name="CheckCircle2" size={16} className="text-green-500" />
                تم الحفظ بنجاح
              </span>
            )}
            {error && (
              <span className="flex items-center gap-1.5 text-sm text-red-600">
                <Icon name="AlertCircle" size={16} className="text-red-500" />
                {error}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">
              {(contents[activePage] || '').length} حرف
            </span>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} color="white" />
                  حفظ التغييرات
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <Icon name="Info" size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 leading-relaxed">
          <strong>تلميح:</strong> يمكنك كتابة المحتوى باستخدام وسوم HTML القياسية. استخدم أزرار الإدراج السريع أعلاه لإضافة العناصر بسهولة، ثم اضغط "معاينة" لرؤية الشكل النهائي قبل الحفظ. التغييرات تظهر فوراً على الصفحات العامة بعد الحفظ.
        </div>
      </div>
    </div>
  );
}
